import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, FileText } from 'lucide-react';

const TermsOfUse = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-sky-50 to-blue-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          data-testid="back-btn"
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-6 rounded-xl"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Crimson Text, serif' }}>
                Terms of Use
              </h1>
              <p className="text-sm text-gray-500">Last updated: {new Date().toLocaleDateString()}</p>
            </div>
          </div>

          <div className="prose max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                1. Acceptance of Terms
              </h2>
              <p className="text-gray-700">
                By creating an account and using DocMarket ("the Platform", "we", "us", or "our"), you agree to be bound by these Terms of Use. If you do not agree to these terms, you may not access or use the Platform. Your continued use of the Platform constitutes your acceptance of any modifications to these terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                2. Platform Purpose and Scope
              </h2>
              <p className="text-gray-700 mb-2">
                DocMarket is a marketplace designed exclusively for students to buy and sell <strong>their own original academic notes, study materials, essays, and educational content</strong> from collegiate institutions (bachelor's degree programs through doctoral programs).
              </p>
              <div className="bg-red-50 border-l-4 border-red-500 p-4 my-4">
                <p className="text-red-900 font-semibold">CRITICAL: ORIGINAL CONTENT ONLY</p>
                <p className="text-red-800 text-sm mt-1">
                  You may ONLY upload content that you personally created. Uploading copyrighted material that you do not own or have explicit permission to distribute is strictly prohibited and will result in immediate account termination.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                3. Account Registration and Eligibility
              </h2>
              <p className="text-gray-700 mb-2">To use DocMarket, you must:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Be at least 13 years of age</li>
                <li>Create an account with accurate information</li>
                <li>Provide a valid email address</li>
                <li>Maintain the confidentiality of your account credentials</li>
                <li>Accept full responsibility for all activity under your account</li>
                <li>Notify us immediately of any unauthorized access to your account</li>
              </ul>
              <p className="text-gray-700 mt-3">
                You may not create an account using false information or impersonate another person or entity.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                4. Strictly Prohibited Content and Activities
              </h2>
              <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-3">
                <p className="text-red-900 font-semibold mb-2">The following are STRICTLY PROHIBITED and will result in IMMEDIATE ACCOUNT TERMINATION:</p>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">4.1 Copyright Infringement</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>Uploading textbooks, published materials, or copyrighted content you do not own</li>
                <li>Sharing course materials created by professors or teaching assistants</li>
                <li>Distributing exam questions or materials provided by instructors</li>
                <li>Any content that infringes on third-party intellectual property rights</li>
              </ul>
              <p className="text-gray-700 italic">
                You represent and warrant that all content you upload is your original work or you have explicit written permission to distribute it.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">4.2 Academic Dishonesty and Cheating</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>Sharing graded assignments, exams, or quizzes intended for individual completion</li>
                <li>Distributing content that facilitates academic dishonesty</li>
                <li>Uploading materials that violate your institution's academic integrity policy</li>
                <li>Content that enables cheating or plagiarism</li>
              </ul>
              <p className="text-gray-700 font-semibold">
                While DocMarket is not responsible for how users employ materials, we will ban any user found to be facilitating academic dishonesty.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">4.3 Pornographic and Adult Content</h3>
              <div className="bg-red-100 border border-red-400 rounded p-3 mb-3">
                <p className="text-red-900 font-bold">
                  ZERO TOLERANCE POLICY: Any pornographic, sexually explicit, or adult content is absolutely prohibited. There is no gray area.
                </p>
              </div>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>Any sexually explicit images, videos, or text</li>
                <li>Nudity or sexual content of any kind</li>
                <li>Links to adult websites or content</li>
              </ul>
              <p className="text-gray-700 font-semibold">
                Violation will result in immediate account termination and may result in legal prosecution under applicable laws.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">4.4 Other Prohibited Content</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Hate speech, harassment, or discriminatory content</li>
                <li>Threats of violence or content promoting illegal activity</li>
                <li>Spam, malware, viruses, or malicious code</li>
                <li>Personal information of others without their consent</li>
                <li>Content that violates any local, state, or federal laws</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                5. Content Ownership and License
              </h2>
              <p className="text-gray-700 mb-3">
                You retain ownership of the content you upload to DocMarket. However, by uploading content, you:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>Grant DocMarket a non-exclusive, worldwide license to host, display, and distribute your content to users who purchase it</li>
                <li>Consent to share your content with the Platform and its users</li>
                <li>Represent that you have all necessary rights to upload and distribute the content</li>
                <li>Accept full responsibility for ensuring your content does not violate any laws or third-party rights</li>
              </ul>
              <p className="text-gray-700 font-semibold">
                Uploading content to DocMarket constitutes your explicit consent to share that information with the site and its users in accordance with these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                6. Payments, Fees, and In-App Purchases
              </h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">6.1 Platform Fees</h3>
              <p className="text-gray-700 mb-2">
                DocMarket charges the following fees to maintain and operate the Platform:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li><strong>Platform Commission:</strong> 15% of the document sale price (added on top of the listed price, paid by the buyer)</li>
                <li><strong>Payment Processing Fee:</strong> Approximately 2.9% + $0.30 per transaction (paid by buyer in addition to document price)</li>
              </ul>
              <p className="text-gray-700 mb-3">
                Sellers receive 100% of their listed price. The 15% platform fee is charged to the buyer on top of the listed price, enabling us to maintain infrastructure, provide customer support, and develop new features.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">6.2 Payment Processing</h3>
              <p className="text-gray-700 mb-3">
                All payments are processed through Stripe, a third-party payment processor. By making a purchase, you agree to Stripe's Terms of Service and Privacy Policy. DocMarket does not store your payment card information.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">6.3 No Refunds Policy</h3>
              <div className="bg-yellow-50 border border-yellow-300 rounded p-3 mb-3">
                <p className="text-yellow-900 font-semibold">
                  ALL SALES ARE FINAL. NO REFUNDS.
                </p>
              </div>
              <p className="text-gray-700 mb-2">
                Due to the digital nature of our products, all purchases are final and non-refundable once the document has been accessed. We encourage buyers to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Carefully review document previews before purchasing</li>
                <li>Read document descriptions and ratings</li>
                <li>Check the seller's profile and reputation</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                7. Platform Responsibility and Disclaimers
              </h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">7.1 User-Generated Content</h3>
              <p className="text-gray-700 mb-3">
                DocMarket is a marketplace platform. We do not create, review, or endorse user-uploaded content. Users are solely responsible for the content they upload and share.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">7.2 Copyright Enforcement</h3>
              <p className="text-gray-700 mb-3">
                While we are not responsible for monitoring all user uploads, we take copyright infringement seriously. If we become aware of copyright violations, we will:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>Remove the infringing content immediately</li>
                <li>Terminate the offending user's account</li>
                <li>Cooperate with legal authorities if necessary</li>
              </ul>
              <p className="text-gray-700">
                To report copyright infringement, contact us at legal@docmarket.io with detailed information.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">7.3 Academic Integrity</h3>
              <p className="text-gray-700 mb-3">
                Users are responsible for ensuring their use of purchased materials complies with their institution's academic integrity policies. DocMarket is not responsible for how users employ the materials they purchase or upload.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                8. Account Termination and Suspension
              </h2>
              <p className="text-gray-700 mb-2">
                We reserve the right to suspend or terminate your account immediately, without prior notice, for:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>Violation of these Terms of Use</li>
                <li>Copyright infringement</li>
                <li>Academic dishonesty or facilitating cheating</li>
                <li>Uploading prohibited content (including pornography)</li>
                <li>Fraudulent activity or payment disputes</li>
                <li>Harassment or abusive behavior</li>
                <li>Any conduct we deem harmful to the Platform or its users</li>
              </ul>
              <p className="text-gray-700">
                Upon termination, you will lose access to your account, purchased content, and any pending earnings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                9. Limitation of Liability
              </h2>
              <p className="text-gray-700 mb-3">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>DocMarket provides the Platform "AS IS" without warranties of any kind</li>
                <li>We are not liable for the accuracy, quality, or legality of user-uploaded content</li>
                <li>We are not responsible for disputes between buyers and sellers</li>
                <li>We are not liable for any indirect, incidental, or consequential damages</li>
                <li>Our total liability to you shall not exceed the amount you paid in fees over the past 12 months</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                10. Indemnification
              </h2>
              <p className="text-gray-700">
                You agree to indemnify, defend, and hold harmless DocMarket, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
                <li>Your use of the Platform</li>
                <li>Content you upload or share</li>
                <li>Violation of these Terms</li>
                <li>Infringement of third-party rights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                11. Governing Law and Dispute Resolution
              </h2>
              <p className="text-gray-700 mb-3">
                These Terms are governed by the laws of the United States and the State of California, without regard to conflict of law principles. Any disputes shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                12. Changes to Terms
              </h2>
              <p className="text-gray-700">
                We may update these Terms at any time. Material changes will be communicated via email or platform notification. Continued use of the Platform after changes constitutes acceptance of the updated Terms. It is your responsibility to review these Terms periodically.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                13. Severability
              </h2>
              <p className="text-gray-700">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                14. DMCA Takedown Policy
              </h2>
              <p className="text-sm text-gray-500 mb-4">Effective Date: January 1, 2026</p>
              <p className="text-gray-700 mb-4">
                DocMarket respects the intellectual property rights of others and expects its users to do the same. In accordance with the Digital Millennium Copyright Act ("DMCA"), it is our policy to respond to valid notices of alleged copyright infringement and, where appropriate, to disable or remove access to material that infringes upon the rights of copyright holders.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">How to File a DMCA Takedown Notice</h3>
              <p className="text-gray-700 mb-3">
                If you are a copyright owner or an agent authorized to act on behalf of one, and you believe that content available through our app or website infringes upon your copyrights, please submit a written DMCA takedown notice containing the following information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Identification of the copyrighted work you claim has been infringed.</li>
                <li>A description or link to the material that is allegedly infringing and its location on the DocMarket platform.</li>
                <li>Your name, mailing address, phone number, and email address.</li>
                <li>A statement: <em>"I have a good faith belief that the use of the material in the manner complained of is not authorized by the copyright owner, its agent, or the law."</em></li>
                <li>A statement: <em>"I swear, under penalty of perjury, that the information in this notice is accurate and that I am the copyright owner or am authorized to act on behalf of the owner."</em></li>
                <li>An electronic or physical signature of the copyright owner or their authorized agent.</li>
              </ul>
              <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 mb-4">
                <p className="text-gray-700 font-semibold mb-1">Please send your DMCA notice to:</p>
                <p className="text-gray-700">Email: <a href="mailto:legal@docmarket.io" className="text-blue-600 hover:underline">legal@docmarket.io</a></p>
                <p className="text-gray-700">Subject Line: "DMCA Takedown Request"</p>
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Counter-Notification Procedure</h3>
              <p className="text-gray-700 mb-3">
                If you believe content you uploaded was removed or disabled in error, you may submit a counter-notification. To be valid, it must include:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li>Your name, address, phone number, and email address.</li>
                <li>Identification of the material removed and its location before removal.</li>
                <li>A statement under penalty of perjury: <em>"I have a good faith belief that the material was removed or disabled as a result of mistake or misidentification."</em></li>
                <li>A statement: <em>"I consent to the jurisdiction of the Federal District Court in the district where my address is located, or if outside the U.S., that I will accept service of process from the party who filed the original DMCA notice."</em></li>
                <li>Your electronic or physical signature.</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Send your counter-notice to the same email: <a href="mailto:legal@docmarket.io" className="text-blue-600 hover:underline">legal@docmarket.io</a>
              </p>
              <p className="text-gray-700 mb-4">
                If we receive a valid counter-notice, we may restore the removed content unless the original claimant notifies us within 10–14 business days that they have filed legal action.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Repeat Infringer Policy</h3>
              <p className="text-gray-700 mb-4">
                In accordance with the DMCA and other applicable laws, DocMarket has adopted a policy of terminating, in appropriate circumstances and at our sole discretion, users who are deemed to be repeat infringers.
              </p>

              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                <p className="text-yellow-900 font-semibold mb-1">Disclaimer</p>
                <p className="text-yellow-800 text-sm">
                  This information is provided solely for informational purposes and is not legal advice. If you are unsure about your rights or obligations, consult an attorney.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                15. Contact Information
              </h2>
              <p className="text-gray-700 mb-2">
                For questions, concerns, or to report violations of these Terms:
              </p>
              <ul className="list-none text-gray-700 space-y-1 mt-2">
                <li><strong>General inquiries:</strong> support@docmarket.io</li>
                <li><strong>Copyright issues:</strong> legal@docmarket.io</li>
                <li><strong>DMCA notices:</strong> legal@docmarket.io</li>
                <li><strong>Report violations:</strong> legal@docmarket.io</li>
              </ul>
            </section>

            <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 mt-6">
              <p className="text-blue-900 font-semibold">
                By using DocMarket, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
